/**
 * Utility for parsing unstructured text to extract tradesperson information
 *
 * Features:
 * - Extracts phone numbers, emails, and postcodes using regex patterns
 * - Prioritizes mobile numbers (07...) when multiple phone numbers are found
 * - Detects trade type based on keyword frequency
 * - Extracts company/personal name using heuristics
 */
import { debug } from '@/lib/utils';

// We'd use a proper phone library in production
// For MVP, using a simplified UK phone regex pattern
const UK_PHONE_REGEX = /(?:(?:\+44|0)(?:\s*\d\s*){9,10})/g;

// Email regex pattern
const EMAIL_REGEX = /[\w.-]+@[\w.-]+\.\w+/g;

// UK Postcode regex pattern (simplified)
const UK_POSTCODE_REGEX = /[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}/gi;

// Common trade type keywords to detect
const TRADE_KEYWORDS: Record<string, string[]> = {
  'plumber': ['plumb', 'plumbing', 'boiler', 'heating', 'water', 'pipe', 'tap', 'toilet', 'bathroom'],
  'electrician': ['electric', 'electrician', 'wiring', 'socket', 'light', 'power'],
  'builder': ['build', 'builder', 'construction', 'extension', 'renovation'],
  'decorator': ['decor', 'paint', 'decorator', 'wallpaper', 'interior'],
  'roofer': ['roof', 'roofer', 'gutter', 'tile', 'slate', 'leak'],
  'gardener': ['garden', 'gardener', 'landscap', 'lawn', 'tree', 'hedge', 'yard'],
  'cleaner': ['clean', 'cleaner', 'domestic', 'house clean', 'window clean'],
  'carpenter': ['carpen', 'wood', 'joiner', 'cabinet', 'furniture']
};

export interface ParsedTradeInfo {
  name: string;
  trade_type: string;
  phone: string;
  email: string;
  postcode: string;
  detectedFields: string[];
}

/**
 * Parse unstructured text to extract tradesperson information
 *
 * @param text The raw text to parse
 * @returns Object containing extracted information and detection status
 */
export function parseTradeInfo(text: string): ParsedTradeInfo {
  // Skip empty text
  if (!text || text.trim().length === 0) {
    return {
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: '',
      detectedFields: []
    };
  }

  debug.log('Parsing trade info text:', text);

  const result: ParsedTradeInfo = {
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: '',
    detectedFields: []
  };

  // Working copy of text that we'll modify as we extract information
  let workingText = text;

  // Extract phone number - prioritize mobile numbers (07...)
  const phoneMatches = Array.from(workingText.matchAll(UK_PHONE_REGEX) || []);
  if (phoneMatches && phoneMatches.length > 0) {
    // Sort phone numbers to prioritize mobile numbers (starting with '07' or '+447')
    const sortedPhones = [...phoneMatches].sort((a, b) => {
      const aText = a[0].replace(/\D/g, ''); // Remove all non-digits
      const bText = b[0].replace(/\D/g, ''); // Remove all non-digits

      const aIsMobile = aText.startsWith('07') || aText.startsWith('447');
      const bIsMobile = bText.startsWith('07') || bText.startsWith('447');

      if (aIsMobile && !bIsMobile) return -1;
      if (!aIsMobile && bIsMobile) return 1;
      return 0;
    });

    // Take the first match after sorting (will be a mobile if available)
    const rawPhone = sortedPhones[0][0].replace(/\D/g, ''); // Remove all non-digits

    // Format the phone number consistently for display
    const phone = rawPhone.startsWith('0')
      ? '+44' + rawPhone.substring(1) // Convert 07... to +447...
      : rawPhone.startsWith('44')
        ? '+' + rawPhone // Convert 447... to +447...
        : '+44' + rawPhone; // Default case - assume UK and add full prefix

    result.phone = phone;
    result.detectedFields.push('phone');
    workingText = workingText.replace(sortedPhones[0][0], ' '); // Remove from working text
  }

  // Extract email
  const emailMatches = workingText.match(EMAIL_REGEX);
  if (emailMatches && emailMatches.length > 0) {
    result.email = emailMatches[0].toLowerCase();
    result.detectedFields.push('email');
    workingText = workingText.replace(emailMatches[0], ' '); // Remove from working text
  }

  // Extract postcode
  const postcodeMatches = workingText.match(UK_POSTCODE_REGEX);
  if (postcodeMatches && postcodeMatches.length > 0) {
    result.postcode = postcodeMatches[0].replace(/\s+/g, '').toUpperCase();
    result.detectedFields.push('postcode');
    workingText = workingText.replace(postcodeMatches[0], ' '); // Remove from working text
  }

  // Detect trade type based on keywords
  const lowercaseText = workingText.toLowerCase();
  let bestTradeMatch = '';
  let bestMatchCount = 0;

  Object.entries(TRADE_KEYWORDS).forEach(([tradeType, keywords]) => {
    const matchCount = keywords.filter(keyword => lowercaseText.includes(keyword)).length;
    if (matchCount > bestMatchCount) {
      bestMatchCount = matchCount;
      bestTradeMatch = tradeType;
    }
  });

  if (bestMatchCount > 0) {
    result.trade_type = bestTradeMatch;
    result.detectedFields.push('trade_type');
  }

  // Extract name (basic heuristic - first line or sentence that's not detected as something else)
  // Clean working text of common symbols and extra whitespace
  // Use a safer approach for emojis and special characters
  workingText = workingText
    // Replace emoji and special characters individually to avoid surrogate pair issues
    .replace(/☎/g, ' ')
    .replace(/☏/g, ' ')
    .replace(/📱/g, ' ')
    .replace(/📞/g, ' ')
    .replace(/🔸/g, ' ')
    .replace(/•/g, ' ')
    .replace(/◦/g, ' ')
    .replace(/‣/g, ' ')
    .replace(/⁃/g, ' ')
    .replace(/⌖/g, ' ')
    .replace(/🔹/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Try to extract name from the first substantial text chunk
  const textChunks = workingText.split(/[.,;:\n]/);
  for (const chunk of textChunks) {
    const cleaned = chunk.trim();
    if (cleaned.length > 2 && cleaned.length < 50) { // Reasonable name length
      result.name = cleaned;
      result.detectedFields.push('name');
      break;
    }
  }

  // If no name was found, use the first part of the text
  if (!result.name && workingText.length > 0) {
    const firstPart = workingText.substring(0, Math.min(30, workingText.length)).trim();
    if (firstPart.length > 0) {
      result.name = firstPart;
      result.detectedFields.push('name');
    }
  }

  debug.log('Parsed trade info:', result);
  return result;
}
